import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';

/** Universal order audit-trail event codes. */
export type OrderHistoryAction =
  | 'ORDER_CREATED'
  | 'COMPANY_ACCEPTED'
  | 'COMPANY_DECLINED'
  | 'PROVIDER_ASSIGNED'
  | 'PROVIDER_ACCEPTED'
  | 'PROVIDER_REJECTED'
  | 'PROVIDER_ON_WAY'
  | 'PROVIDER_ARRIVED'
  | 'PROVIDER_STARTED'
  | 'PROVIDER_COMPLETED'
  | 'ORDER_CANCELLED'
  | 'SERVICE_TIME_CONFIRMED'
  | 'PROVIDER_REASSIGNED'
  | 'ADMIN_FORCE_CANCEL'
  | 'ADMIN_FORCE_COMPLETE'
  | 'ADMIN_REASSIGNMENT'
  | 'ADMIN_OVERRIDE_TIME'
  | string;

export type OrderHistoryActorRole =
  | 'customer'
  | 'company_admin'
  | 'provider'
  | 'system'
  | 'super_admin';

export interface OrderHistoryActor {
  id: string;
  role: OrderHistoryActorRole;
  name: string;
}

export interface OrderHistoryEntry {
  timestamp: Date;
  status: string;
  action: OrderHistoryAction;
  message: string;
  actor: OrderHistoryActor;
  changes: Record<string, unknown> | null;
  /** Legacy field kept for older UI that reads `date`. */
  date: Date;
}

export interface BuildOrderHistoryParams {
  action: OrderHistoryAction;
  status: string;
  message: string;
  actor: Partial<OrderHistoryActor> & { role: OrderHistoryActorRole };
  changes?: Record<string, unknown> | null;
  timestamp?: Date;
}

export function buildOrderHistoryEntry(
  params: BuildOrderHistoryParams
): OrderHistoryEntry {
  const now = params.timestamp || new Date();
  return {
    timestamp: now,
    date: now,
    status: String(params.status || ''),
    action: params.action,
    message: String(params.message || ''),
    actor: {
      id: String(params.actor?.id || ''),
      role: params.actor.role,
      name: String(params.actor?.name || params.actor.role),
    },
    changes: params.changes ?? null,
  };
}

export function orderHistoryArrayUnion(entry: OrderHistoryEntry) {
  return firebase.firestore.FieldValue.arrayUnion(entry);
}

export function withOrderHistory(
  patch: Record<string, unknown>,
  ...entries: OrderHistoryEntry[]
): Record<string, unknown> {
  return {
    ...patch,
    history: firebase.firestore.FieldValue.arrayUnion(...entries),
  };
}

export function actorDisplayName(
  user: {
    fname?: string;
    lname?: string;
    name?: string;
    displayName?: string;
  } | null
    | undefined,
  fallback = 'Unknown'
): string {
  if (!user) {
    return fallback;
  }
  const full = `${user.fname || ''} ${user.lname || ''}`.trim();
  return full || user.name || user.displayName || fallback;
}
